/**
 * "Start from a template" — the last card in the main column, as the approved
 * design has it.
 *
 * WHAT IT DRAWS
 * -----------------------------------------------------------------------------
 * Four tiles across, each an image swatch over a name and one mono line reading
 * `Email · used 4×`. The design's swatches are brand gradients; the workspace
 * owner asked for abstract photography instead, so each tile carries an
 * Unsplash image — see `TEMPLATE_SWATCHES`.
 *
 * WHY THIS CARD EXISTS AT ALL
 * -----------------------------------------------------------------------------
 * There is a gap between what onboarding calls done and what the campaign form
 * will accept. `STEP_PROBES.build_template` ticks on a template of **any**
 * status; the campaign form lists `status = 'approved'` only. So a workspace
 * with five drafts finishes the checklist, watches `OnboardingPanel` disappear,
 * and discovers on the campaign form that its picker is empty. This card says
 * it one screen earlier, and in the state where it has nothing to list it says
 * it in the one sentence that repairs it.
 *
 * WHY A TILE OPENS THE CAMPAIGN FORM
 * -----------------------------------------------------------------------------
 * The title is an imperative, so the destination was made real rather than the
 * title softened: `/campaigns/new?template=<id>` opens the form with that
 * template already chosen. See the long comment on `NewCampaignPage`, which
 * also says why the parameter is honoured only when it names a template that
 * form actually offers. If that parameter is ever removed, this card's title
 * has to change in the same commit.
 *
 * THE EMPTY STATE IS NOT THE ERROR STATE
 * -----------------------------------------------------------------------------
 * `useAsyncData` leaves `data` at `null` when a read fails, so "nothing is
 * approved" and "the request failed" arrive in the same shape unless something
 * separates them. This card's empty state is an accusation — it tells a reader
 * their templates are unfinished — and levelling that at somebody whose network
 * dropped is the zero-versus-not-measured rule in its sharpest form.
 *
 * The empty sentence is careful about what it does not know. The query filters
 * to `type: 'email'` and `status: 'approved'`, so an empty list is silence
 * about drafts, about WhatsApp templates, and about everything else this card
 * did not ask for. It says no email template is *approved* — never "you have no
 * templates", which would be a claim made out of the card's own ignorance.
 */

import { Card, CardContent, CardHeader, CardTitle, cn } from '@wend/ui';
import { Link } from 'react-router-dom';
import type { TemplateListResponse } from '@wend/shared';

import { ErrorState } from '../../components/ErrorState';
import { formatCount } from '../../lib/format';
import type { AsyncData } from '../../lib/use-async-data';

/** Four across at desktop, so four is what is asked for and four is drawn. */
export const TEMPLATE_ROWS = 4;

/**
 * ABSTRACT PHOTOGRAPHY INSTEAD OF THE DESIGN'S GRADIENTS.
 *
 * The mockup paints each swatch with a two-stop brand gradient. The workspace
 * owner asked for abstract images, so these are Unsplash photographs chosen to
 * sit in the same palette the gradients did — violet, cyan, magenta, amber —
 * which is what keeps the row reading as one set rather than four stock photos.
 *
 * **Assigned by position, not by template.** A template has no artwork of its
 * own, and picking by name or id would mean the same template changed picture
 * when another was approved above it. Position is stable within a render, and
 * it is honestly decorative rather than pretending to describe the template.
 *
 * Remote, not bundled: these are decoration, and four JPEGs have no place in
 * the app bundle. Each tile also carries a flat `tint` behind its image, so a
 * blocked or slow CDN costs the swatch its photograph and not its colour.
 */
export const TEMPLATE_SWATCHES: readonly { readonly id: string; readonly tint: string }[] = [
  { id: 'photo-1635776062127-d379bfcba9f8', tint: 'rgba(109,94,246,.55)' },
  { id: 'photo-1554034483-04fda0d3507b', tint: 'rgba(76,201,240,.45)' },
  { id: 'photo-1604076913837-52ab5629fba9', tint: 'rgba(242,112,138,.45)' },
  { id: 'photo-1644426358812-879f02d1d867', tint: 'rgba(245,184,81,.45)' },
];

export function swatchUrl(index: number): string {
  const photo = TEMPLATE_SWATCHES[index % TEMPLATE_SWATCHES.length];
  return `https://images.unsplash.com/${photo?.id ?? ''}?w=320&h=128&fit=crop&q=70&auto=format`;
}

/**
 * `Email · used 4×`, and `never used` rather than `used 0×`.
 *
 * Zero uses is a real, measured zero — `useCount` is a count over campaigns
 * that actually sent — so this is not the not-measured case. It is spelled in
 * words because "used 0×" reads as a defect in the counter rather than a fact
 * about the template, and because it is the same phrase the design uses.
 */
export function templateMeta(type: string, useCount: number): string {
  const channel = type === 'whatsapp' ? 'WhatsApp' : 'Email';
  return useCount === 0 ? `${channel} · never used` : `${channel} · used ${formatCount(useCount)}×`;
}

export function TemplatesCard({ templates }: { templates: AsyncData<TemplateListResponse> }) {
  const { data, error, status, reload } = templates;

  const failedOutright = status === 'error' && data === null;
  if (data === null && !failedOutright) return null;

  const items = data?.items ?? [];

  return (
    <Card data-role="templates-card">
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle
          className="text-[0.95rem]"
          sub={data === null ? undefined : `${formatCount(data.pagination.total)} ready`}
        >
          Start from a template
        </CardTitle>
      </CardHeader>

      <CardContent className="p-[18px] pt-0">
        {failedOutright && (
          <ErrorState
            error={error}
            title="Could not load your templates"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}

        {/*
          A refresh that failed over tiles already on screen keeps the tiles.
          The card's own title sits directly above, so the shorter noun is used.
        */}
        {status === 'error' && data !== null && (
          <ErrorState
            error={error}
            title="Could not refresh"
            onRetry={reload}
            className="mb-3 flex flex-col items-start gap-2 rounded-base border border-line px-4 py-3"
          />
        )}

        {data !== null && items.length === 0 && (
          <p
            data-role="templates-empty"
            className="rounded-base border border-dashed border-line-strong px-4 py-4 text-center text-[0.78rem] leading-relaxed text-muted"
          >
            No email template is approved yet, so the New campaign form has none to offer —{' '}
            <Link to="/templates" className="text-text underline underline-offset-2">
              open Templates
            </Link>{' '}
            to finish one and approve it.
          </p>
        )}

        {items.length > 0 && (
          <div data-role="template-list" className="grid grid-cols-2 gap-[11px] md:grid-cols-4">
            {items.map((row, index) => (
              <Link
                key={row.id}
                data-role="template"
                to={`/campaigns/new?template=${encodeURIComponent(row.id)}`}
                className={cn(
                  'overflow-hidden rounded-base border border-line bg-white/[0.03]',
                  'hover:border-white/25',
                )}
              >
                <span
                  aria-hidden="true"
                  className="block h-16 bg-cover bg-center"
                  style={{
                    backgroundColor: TEMPLATE_SWATCHES[index % TEMPLATE_SWATCHES.length]?.tint,
                    backgroundImage: `url("${swatchUrl(index)}")`,
                  }}
                />
                <span className="block px-[11px] py-[9px]">
                  <span className="block truncate text-[0.78rem] font-medium text-text">
                    {row.name}
                  </span>
                  <span className="mt-0.5 block font-mono text-[0.62rem] text-muted">
                    {templateMeta(row.type, row.useCount)}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
